import { useTranslation } from "react-i18next"

import { BadgeMedallionStrip } from "@/components/badges/badge-medallion-strip"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type MedallionViewModel,
  medallionName,
  medallionPopover,
  toMedallionViewModels,
} from "@/lib/badge-medallions"
import type { BadgeMedallionRow } from "@/types/badges"

/**
 * OKS-969 — "Badges obtenus" card on `Brand/Crew/Show` (left column, directly
 * above the Kudos card: earned recognition then authored recognition reads as
 * one narrative).
 *
 * This component exists to keep the Card chrome and EVERY `t()` call OUT of
 * `<BadgeMedallionStrip>`. That separation is what lets OKS-970 drop the strip
 * into a 64px roster cell without a Card to fight and without inheriting this
 * page's brand-facing copy — the strip is hermetic, and the host owns the
 * words.
 *
 * The card renders even when the creator has no badges (BRIEF D8/AC5): hiding
 * it would leave a brand admin unsure whether badges exist at all, and a
 * padlock placeholder would import the creator goal board's motivational
 * framing onto someone who has no goal here. Same posture as
 * `<CreatorCompletedMasterclassesSection>`.
 */
export interface CreatorBadgesSectionProps {
  badges: BadgeMedallionRow[]
}

/** BRIEF D6 — a product cap on when trophies start reading as clutter. */
const MAX_VISIBLE = 8

export function CreatorBadgesSection({ badges }: CreatorBadgesSectionProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || "fr"

  // Server order is preserved: `toMedallionViewModels` maps, it does not sort.
  const items = toMedallionViewModels(badges)

  const nameOf = (vm: MedallionViewModel) => medallionName(vm, t)

  return (
    <Card data-testid="creator-detail-badges-section">
      <CardHeader>
        <CardTitle>{t("crew.detail.badges.section_title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <BadgeMedallionStrip
          items={items}
          size="preview"
          max={MAX_VISIBLE}
          labels={{
            strip: t("crew.detail.badges.section_title"),
            empty: t("badges.medallions.empty"),
            // BRIEF AC8 — a repeated family MUST speak its count, because the
            // `×N` chip is aria-hidden and would otherwise exist only as a
            // glyph. Single-award families reuse the shipped brief key rather
            // than minting a parallel sentence.
            medallion: (vm) =>
              vm.count > 1
                ? t("badges.aria.tile_earned_repeated", {
                    name: nameOf(vm),
                    tier: t(`badges.tier.${vm.tier}`),
                    count: vm.count,
                  })
                : t("badges.aria.tile_earned_brief", {
                    name: nameOf(vm),
                    tier: t(`badges.tier.${vm.tier}`),
                  }),
            countChip: (count) => t("badges.medallions.count_chip", { count }),
            // OKS-975 — the resting-state affordance (AC6). Supplied HERE and
            // not on the roster: this strip is interactive, so the line is
            // true here and would be a lie there.
            hint: t("badges.medallions.popover_hint"),
            // OKS-975 — the rule lives in `medallionPopover` now. This was an
            // inline copy of it, and the roster had the other; see that
            // function's header for why one of them was unobservable.
            popover: (vm) => medallionPopover(vm, t, locale),
            more: (hidden) =>
              t("badges.medallions.show_more", { count: hidden }),
            moreAria: (hidden) =>
              t("badges.medallions.show_more_aria", { count: hidden }),
            less: t("badges.medallions.show_less"),
          }}
        />
      </CardContent>
    </Card>
  )
}
